import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { productsApi } from "../../../../api/products";
import { formatApiError } from "../../../../api/client";
import ImportSourceStep from "./ImportSourceStep";
import ImportSampleStep from "./ImportSampleStep";
import ImportMappingStep from "./ImportMappingStep";
import ImportDefaultsStep from "./ImportDefaultsStep";
import ImportPreviewStep from "./ImportPreviewStep";
import ImportResultStep from "./ImportResultStep";
import { deriveImportColumnSamples, mappedTarget, suggestImportMapping } from "./importFields";

const STEPS = ["Fuente", "Muestra", "Mapping", "Defaults y modo", "Preview", "Confirmación"];
const INITIAL_DEFAULTS = {
  category: "General",
  unit: "",
  itemKind: "product",
  active: true,
  publicVisible: true,
  sellable: true,
  purchasable: true,
};
const REQUIRED_DESTINATION_FIELDS = [
  "name",
  "category",
  "price",
  "stock",
  "code",
  "unit",
  "itemKind",
  "active",
  "publicVisible",
  "sellable",
  "purchasable",
];
const REQUIRED_MODES = ["create_only", "upsert_by_code"];

function contractError(message) {
  const error = new Error(message);
  error.code = "PRODUCT_IMPORT_CONTRACT_INVALID";
  return error;
}

function assertDraftContract(response) {
  const importId = String(response?.importId || "").trim();
  const headers = Array.isArray(response?.headers) ? response.headers.map(String) : [];
  const rowCount = Number(response?.rowCount);
  const destinationFields = response?.contract?.destinationFields;
  const modes = response?.contract?.modes;

  if (!importId) throw contractError("El backend no devolvió un importId válido para el borrador.");
  if (!headers.length || !Number.isFinite(rowCount) || rowCount < 1) {
    throw contractError("El backend devolvió una muestra de importación incompleta.");
  }
  if (!destinationFields || typeof destinationFields !== "object" || Array.isArray(destinationFields)) {
    throw contractError("El backend no devolvió el contrato de campos del importador.");
  }

  const missingFields = REQUIRED_DESTINATION_FIELDS.filter((field) => !destinationFields[field]);
  if (missingFields.length) {
    throw contractError(`El contrato del importador no soporta: ${missingFields.join(", ")}.`);
  }
  if (!Array.isArray(modes) || REQUIRED_MODES.some((mode) => !modes.includes(mode))) {
    throw contractError("El contrato del importador no soporta los modos requeridos por el panel.");
  }

  return { importId, headers, rowCount, contract: response.contract };
}

function assertPreviewContract(response, importId) {
  const responseImportId = String(response?.importId || "").trim();
  if (!responseImportId || responseImportId !== String(importId || "").trim()) {
    throw contractError("La vista previa no corresponde al borrador de importación activo.");
  }

  const totals = response?.totals;
  const totalKeys = ["rows", "created", "updated", "skipped", "errors"];
  if (
    !totals ||
    typeof totals !== "object" ||
    totalKeys.some((key) => !Number.isFinite(Number(totals[key])) || Number(totals[key]) < 0)
  ) {
    throw contractError("El backend devolvió totales inválidos en la vista previa.");
  }
  if (!Array.isArray(response?.rows) || !Array.isArray(response?.errors)) {
    throw contractError("El backend devolvió una vista previa con filas o errores en un formato inválido.");
  }
  if (response?.stockPolicy?.directWrite !== false) {
    throw contractError("La política de stock del preview no garantiza conciliación mediante movimientos ERP.");
  }
  if (typeof response?.canCommit !== "boolean" || !Array.isArray(response?.blockingErrors)) {
    throw contractError("El backend no informó si la preview está habilitada para commit.");
  }

  return true;
}

function assertCommitContract(response, importId) {
  const responseImportId = String(response?.importId || "").trim();
  if (!responseImportId || responseImportId !== String(importId || "").trim()) {
    throw contractError("El resultado del commit no corresponde al borrador activo.");
  }
  if (!["completed", "failed"].includes(String(response?.status || ""))) {
    throw contractError("El backend devolvió un estado final de importación inválido.");
  }
  const totals = response?.totals;
  if (!totals || ["rows", "created", "updated", "skipped", "errors", "stockMovements"].some(
    (key) => !Number.isFinite(Number(totals[key])) || Number(totals[key]) < 0,
  )) {
    throw contractError("El backend devolvió un resumen final inválido.");
  }
  if (!Array.isArray(response?.errors) || !response?.stock || !Array.isArray(response.stock.movements)) {
    throw contractError("El backend devolvió un resultado final incompleto.");
  }
  return true;
}

export default function ProductImportWizard({ open, projectId, onClose, onImported }) {
  const fullScreen = useMediaQuery("(max-width:699.95px)", { noSsr: true });
  const [step, setStep] = React.useState(0);
  const [sourceMode, setSourceMode] = React.useState("file");
  const [file, setFile] = React.useState(null);
  const [googleUrl, setGoogleUrl] = React.useState("");
  const [draft, setDraft] = React.useState(null);
  const [contract, setContract] = React.useState(null);
  const [mapping, setMapping] = React.useState({});
  const [automaticSuggestions, setAutomaticSuggestions] = React.useState({});
  const [defaults, setDefaults] = React.useState(INITIAL_DEFAULTS);
  const [mode, setMode] = React.useState("create_only");
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const importIdRef = React.useRef("");
  const draftProjectIdRef = React.useRef("");
  const committingRef = React.useRef(false);

  const resetState = React.useCallback(() => {
    setStep(0);
    setSourceMode("file");
    setFile(null);
    setGoogleUrl("");
    setDraft(null);
    setContract(null);
    setMapping({});
    setAutomaticSuggestions({});
    setDefaults(INITIAL_DEFAULTS);
    setMode("create_only");
    setPreview(null);
    setResult(null);
    committingRef.current = false;
    setBusy(false);
    setError("");
  }, []);

  const discardDraft = React.useCallback(async () => {
    const importId = importIdRef.current;
    const draftProjectId = draftProjectIdRef.current;
    importIdRef.current = "";
    draftProjectIdRef.current = "";
    if (!importId || !draftProjectId) return;
    try {
      await productsApi.deleteImport(draftProjectId, importId);
    } catch {
      // Best effort: el draft además expira por TTL en backend.
    }
  }, []);

  React.useEffect(() => () => {
    const importId = importIdRef.current;
    const draftProjectId = draftProjectIdRef.current;
    if (importId && draftProjectId) {
      void productsApi.deleteImport(draftProjectId, importId).catch(() => {});
      importIdRef.current = "";
      draftProjectIdRef.current = "";
    }
  }, []);

  const handleClose = React.useCallback(() => {
    if (busy) return;
    void discardDraft();
    resetState();
    onClose?.();
  }, [busy, discardDraft, onClose, resetState]);

  const createDraft = async () => {
    setBusy(true);
    setError("");
    try {
      await discardDraft();
      const response = await productsApi.createImport(
        projectId,
        sourceMode === "file" ? { file } : { url: googleUrl },
      );

      const responseImportId = String(response?.importId || "").trim();
      if (responseImportId) {
        importIdRef.current = responseImportId;
        draftProjectIdRef.current = projectId;
      }

      const validated = assertDraftContract(response);
      const columnSamples = deriveImportColumnSamples({
        headers: validated.headers,
        columnSamples: response?.columnSamples,
        sampleRows: response?.sampleRows,
      });
      const suggestedMapping = suggestImportMapping(validated.headers, columnSamples);

      setDraft(response);
      setContract(validated.contract);
      setFile(null);
      setMapping(suggestedMapping);
      setAutomaticSuggestions(Object.fromEntries(
        Object.entries(suggestedMapping).filter(([, target]) => target && target !== "ignore"),
      ));
      setPreview(null);
      setResult(null);
      setStep(1);
    } catch (err) {
      if (String(err?.code || "") === "PRODUCT_IMPORT_CONTRACT_INVALID") {
        await discardDraft();
      }
      setError(formatApiError(err, "No se pudo preparar la importación"));
    } finally {
      setBusy(false);
    }
  };

  const mappingHasName = Boolean(mappedTarget(mapping, "name"));
  const codeMapped = Boolean(mappedTarget(mapping, "code"));
  const columnSamples = React.useMemo(() => deriveImportColumnSamples({
    headers: draft?.headers || [],
    columnSamples: draft?.columnSamples,
    sampleRows: draft?.sampleRows || [],
  }), [draft]);

  React.useEffect(() => {
    if (!codeMapped && mode === "upsert_by_code") setMode("create_only");
  }, [codeMapped, mode]);

  const updateMapping = React.useCallback((source, target) => {
    setMapping((current) => ({ ...current, [source]: target }));
    setAutomaticSuggestions((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, source)) return current;
      const next = { ...current };
      delete next[source];
      return next;
    });
    setPreview(null);
    setResult(null);
  }, []);

  const updateDefaults = React.useCallback((field, value) => {
    setDefaults((current) => ({ ...current, [field]: value }));
    setPreview(null);
    setResult(null);
  }, []);

  const updateMode = React.useCallback((value) => {
    setMode(value);
    setPreview(null);
    setResult(null);
  }, []);

  const generatePreview = async () => {
    if (!draft?.importId || !mappingHasName) return;
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      const response = await productsApi.previewImport(projectId, draft.importId, {
        mapping,
        defaults,
        mode,
      });
      assertPreviewContract(response, draft.importId);
      setPreview(response);
      setResult(null);
      setStep(4);
    } catch (err) {
      setError(formatApiError(err, "No se pudo generar la vista previa"));
    } finally {
      setBusy(false);
    }
  };

  const previewIsCurrent = React.useMemo(() => {
    if (!preview || !draft?.importId) return false;
    try {
      return assertPreviewContract(preview, draft.importId);
    } catch {
      return false;
    }
  }, [draft?.importId, preview]);

  const previewCanCommit = previewIsCurrent && preview?.canCommit === true;

  const commitImport = async () => {
    if (!draft?.importId || !previewCanCommit || committingRef.current) return;
    committingRef.current = true;
    setBusy(true);
    setError("");
    try {
      const response = await productsApi.commitImport(projectId, draft.importId);
      assertCommitContract(response, draft.importId);
      setResult(response);
      await onImported?.();
    } catch (err) {
      const failedResult = err?.data?.status === "failed" ? err.data : null;
      if (failedResult) {
        try {
          assertCommitContract(failedResult, draft.importId);
          setResult(failedResult);
          await onImported?.();
        } catch (contractErr) {
          setError(formatApiError(contractErr, "El commit devolvió un resultado inválido"));
        }
      } else {
        setError(formatApiError(err, "No se pudo confirmar la importación"));
      }
    } finally {
      committingRef.current = false;
      setBusy(false);
    }
  };

  const goBack = () => {
    setError("");
    setStep((current) => Math.max(0, current - 1));
  };

  const goForward = async () => {
    setError("");
    if (step === 1) setStep(2);
    else if (step === 2 && mappingHasName) setStep(3);
    else if (step === 3) await generatePreview();
    else if (step === 4 && previewCanCommit) setStep(5);
  };

  const renderStep = () => {
    if (step === 0) {
      return (
        <ImportSourceStep
          sourceMode={sourceMode}
          onSourceModeChange={setSourceMode}
          file={file}
          onFileChange={setFile}
          googleUrl={googleUrl}
          onGoogleUrlChange={setGoogleUrl}
          busy={busy}
          onContinue={createDraft}
        />
      );
    }
    if (step === 1) return <ImportSampleStep draft={draft} />;
    if (step === 2) {
      return (
        <ImportMappingStep
          headers={draft?.headers || []}
          mapping={mapping}
          automaticSuggestions={automaticSuggestions}
          columnSamples={columnSamples}
          contract={contract}
          onChange={updateMapping}
        />
      );
    }
    if (step === 3) {
      return (
        <ImportDefaultsStep
          defaults={defaults}
          onDefaultsChange={updateDefaults}
          mode={mode}
          onModeChange={updateMode}
          codeMapped={codeMapped}
        />
      );
    }
    if (step === 4) return <ImportPreviewStep preview={preview} />;
    return (
      <ImportResultStep
        preview={preview}
        result={result}
        busy={busy}
        onCommit={commitImport}
      />
    );
  };

  const showFooterNext = step > 0 && step < 5;
  const footerNextDisabled = busy || (step === 2 && !mappingHasName) || (step === 4 && !previewCanCommit);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { minHeight: fullScreen ? "100dvh" : "min(820px, calc(100dvh - 48px))" } }}
    >
      <DialogTitle sx={{ pr: 7, pb: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>IMPORTADOR DE ÍTEMS</Typography>
        <Typography variant="h5" sx={{ fontWeight: 950 }}>Importar ítems</Typography>
        <IconButton
          aria-label="Cerrar importador"
          onClick={handleClose}
          disabled={busy}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      {busy ? <LinearProgress /> : null}

      <DialogContent dividers sx={{ bgcolor: "#F7F9FB", p: { xs: 1.5, sm: 2.5 } }}>
        <Stack spacing={2.5}>
          <Box sx={{ overflowX: "auto", pb: 0.5 }}>
            <Stepper activeStep={step} sx={{ minWidth: 720 }}>
              {STEPS.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {busy && !error ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Procesando…</Typography>
            </Stack>
          ) : null}

          <Box sx={{ bgcolor: "background.paper", borderRadius: 3, p: { xs: 1.5, sm: 2.5 }, border: "1px solid", borderColor: "divider" }}>
            {renderStep()}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.75, justifyContent: "space-between" }}>
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Stack direction="row" spacing={1}>
          {step > 0 && !(step === 5 && result) ? (
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={goBack} disabled={busy} sx={{ textTransform: "none" }}>
              Atrás
            </Button>
          ) : null}
          {showFooterNext ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={goForward}
              disabled={footerNextDisabled}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              {step === 3 ? "Generar preview" : step === 4 ? "Revisar confirmación" : "Continuar"}
            </Button>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
